import { NavLink } from "react-router-dom";
import clsx from "clsx";
import { useHealth } from "@/hooks/useHealth";
import { useTheme } from "@/hooks/useTheme";
import { useConnectedWallets } from "@/wallet/useConnectedWallets";
import { truncateMiddle } from "@/lib/truncate";
import { KIND_ICON, MoonIcon, OmniBridgeLogo, SunIcon } from "@/components/icons";

const NAV: Array<{ to: string; label: string }> = [
  { to: "/", label: "Connect" },
  { to: "/recovery", label: "Recovery" },
  { to: "/pause", label: "Pause control" },
];

export function Header() {
  const { network } = useHealth();
  const { theme, toggle: toggleTheme } = useTheme();
  const { connected, disconnectAll } = useConnectedWallets();
  const count = connected.length;

  return (
    <header className="sticky top-0 z-40 border-b border-slate-200 dark:border-slate-800 bg-white/85 dark:bg-slate-900/85 backdrop-blur supports-[backdrop-filter]:bg-white/75 dark:supports-[backdrop-filter]:bg-slate-900/75">
      <nav className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 sm:gap-x-6 sm:px-6">
        <NavLink to="/" className="flex shrink-0 items-center gap-2 text-slate-900 dark:text-slate-100 hover:opacity-90">
          <OmniBridgeLogo className="h-6 w-6" />
          <span className="font-semibold tracking-tight">OmniBridge Ops</span>
        </NavLink>
        {/* On phones the links drop to their own full-width row below the logo +
            status row; inline on sm+ (avoids the navbar overflowing horizontally). */}
        <div className="order-last flex w-full items-center gap-3 sm:order-none sm:w-auto sm:gap-5">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              className={({ isActive }) =>
                clsx(
                  "relative inline-flex items-center px-1 py-1 text-sm font-medium border-b-2 -mb-px transition-colors",
                  isActive
                    ? "text-slate-900 dark:text-slate-100 border-near-green"
                    : "text-slate-600 dark:text-slate-400 border-transparent hover:text-slate-900 dark:hover:text-slate-100 hover:border-slate-300 dark:hover:border-slate-700",
                )
              }
            >
              {n.label}
            </NavLink>
          ))}
        </div>
        <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-2" data-testid="navbar-status">
          <button
            type="button"
            onClick={toggleTheme}
            className="rounded-md border border-slate-300 dark:border-slate-700 p-1 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
            title={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
            aria-label="Toggle color theme"
            data-testid="theme-toggle"
          >
            {theme === "dark" ? (
              <SunIcon className="h-4 w-4" />
            ) : (
              <MoonIcon className="h-4 w-4" />
            )}
          </button>
          <span
            className={clsx(
              "rounded-md px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider border",
              network === "mainnet"
                ? "bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-200 border-rose-300 dark:border-rose-800"
                : "bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 border-amber-300 dark:border-amber-800/60",
            )}
            data-testid="network-chip"
          >
            {network}
          </span>
          {count === 0 ? (
            // Hidden on phones — the network chip + toggle already fit there and
            // the Connect page is where wallets get linked; keeps the bar to two
            // rows on small screens.
            <span className="hidden text-xs text-slate-500 sm:inline" data-testid="no-wallets">
              No wallets connected
            </span>
          ) : (
            <div className="flex items-center gap-1">
              {connected.map(({ kind, account }) => {
                const Icon = KIND_ICON[kind];
                return (
                  <span
                    key={kind}
                    className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 font-mono text-[10px] text-slate-800 dark:text-slate-200"
                    title={`${kind} — ${account}`}
                    data-wallet-chip={kind}
                  >
                    {Icon ? <Icon className="h-3 w-3" /> : null}
                    {truncateMiddle(account, 4, 4)}
                  </span>
                );
              })}
              <button
                type="button"
                onClick={() => void disconnectAll()}
                className="ml-1 rounded-md border border-slate-300 dark:border-slate-700 px-2 py-0.5 text-[11px] text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                data-testid="disconnect-all"
              >
                {count === 1 ? "Disconnect" : "Disconnect all"}
              </button>
            </div>
          )}
        </div>
      </nav>
    </header>
  );
}
